import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  resetState,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/GeneratedCertificateSlice";
import { AppDispatch, RootState } from "src/redux/store";
import { formatDateAndTime } from "src/utils/dateAndTime";
import GeneratedCertificate from "./GeneratedCertificate";
import { getColumns } from "src/utils/auth/getColumns";
import {
  useGetGeneratedCertificatesQuery,
  useExportGeneratedCertificatesXlsxMutation,
} from "src/services/AffidavitCertificateService";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import { AffidavitCertificateUpdateListResponse } from "src/models/AffidavitCertificateUpdate.model";
import { Tooltip } from "@mui/material";
import { showToast } from "src/utils/toaster/showToast";
import moment from "moment";
import DownloadDialogWrapper from "../list/Dialog/DownloadDialogBox/DownloadDialogWrapper";

const paramList = [
  "category",
  "name",
  "email",
  "adhaarNumber",
  "mobileNumber",
  "distributorCode",
  "appliedBy",
  "appliedAs",
  "txnId",
  "appliedFrom",
  "paymentCategory",
  "version",
  "panNumber",
  "acknowledgementNumber",
  "status",
  "assignedTo",
  "assignedBy",
];

const GeneratedCertificateWrapper = () => {
  // Row whose affidavit is being updated (Update Affidavit dialog).
  const [updateRow, setUpdateRow] =
    useState<AffidavitCertificateUpdateListResponse | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const generatedCertificateState: any = useSelector(
    (state: RootState) => state.generatedCertificate
  );
  const {
    items,
    searchValue,
    rowsPerPage,
    page,
    filterBy,
    dateFilter,
    generatedDateFilter,
    orderByValue,
  } = generatedCertificateState;

  useEffect(() => {
    dispatch(resetState());
  }, []);

  // Get Data Query
  const { data, isFetching, isLoading } = useGetGeneratedCertificatesQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: paramList,
    page: page,
    filterBy: filterBy,
    dateFilter: dateFilter,
    orderBy: "createdDateToShow",
    orderByValue: orderByValue,
    isPaginationRequired: true,
    generatedDateFilter: generatedDateFilter,
  });

  // Setting data
  React.useEffect(() => {
    if (!isFetching && !isLoading) {
      dispatch(setIsTableLoading(false));
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }
  }, [isLoading, isFetching, data]);

  // Export XLSX
  const [exportXlsx, { isLoading: isExporting }] =
    useExportGeneratedCertificatesXlsxMutation();

  const handleExportXlsx = async () => {
    try {
      const response: any = await exportXlsx({
        limit: 0,
        searchValue: searchValue,
        params: paramList,
        page: 1,
        filterBy: filterBy,
        dateFilter: dateFilter,
        orderBy: "createdDateToShow",
        orderByValue: orderByValue,
        isPaginationRequired: false,
        generatedDateFilter: generatedDateFilter,
        startSerialNumber: 1,
      });

      if (response?.data) {
        // Create blob from response data
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Affidavit_Certificates_${moment()
            .format("DD-MM-YYYY_HH-mm")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showToast("success", "XLSX exported successfully");
      } else {
        showToast("error", "Failed to export XLSX");
      }
    } catch (error) {
      console.error("Error exporting XLSX:", error);
      showToast("error", "Failed to export XLSX");
    }
  };

  // Download PDF handler
  const downloadPdfFile = async (pdfUrl: string) => {
    try {
      const response = await fetch(pdfUrl, { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to fetch the PDF file.");
      }
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const segments = pdfUrl.split(/[\/?#]/);
      const fileName = segments[segments.length - 1] || "downloaded-file.pdf";
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const columns: columnTypes[] = [
    {
      field: "createdDateToShow",
      headerName: "Date - Time",
      flex: "flex-[1_1_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatDateAndTime(row?.createdDateToShow, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatDateAndTime(row?.createdDateToShow, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "srn",
      headerName: "SRN",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return (
          <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
            {row.srn}
          </span>
        );
      },
    },
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => (
        <span className="text-ellipsis overflow-hidden"> {row.name} </span>
      ),
    },
    {
      field: "parentName",
      headerName: "Father Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => (
        <span className="text-ellipsis overflow-hidden">
          {row.parentName || "N/A"}
        </span>
      ),
    },
    {
      field: "mobileNumber",
      headerName: "Mobile",
      flex: "flex-[1_1_0%]",
    },
    {
      noAuthRequired: true,
      field: "distributorCode",
      headerName: "SJBT Code",
      flex: "flex-[1_1_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return <span> {row.distributorCode || "N/A"} </span>;
      },
    },
    {
      field: "acknowledgementNumber",
      headerName: "Acknowledgement No.",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return (
          <Tooltip title={row.acknowledgementNumber}>
            <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
              {row.acknowledgementNumber || "-"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      noAuthRequired: true,
      field: "generatedDate",
      headerName: "Generated Date",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const certDate =
          row?.affidavitcertificatesData?.createdAt || "";
        return (
          <div>
            <div className="text-slate-700 font-medium">
              {certDate
                ? formatDateAndTime(certDate, "DD MMM yyyy")
                : "-"}
            </div>
            {certDate && (
              <div className="text-[13px] font-medium text-slate-400">
                {formatDateAndTime(certDate, "hh:mm A")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      noAuthRequired: true,
      field: "action",
      headerName: "Download",
      flex: "flex-[0_0_140px]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        const handleDownload = () => {
          if (row?.affidavitcertificatesData?.affidavitertificateFile) {
            downloadPdfFile(
              row.affidavitcertificatesData.affidavitertificateFile
            );
          } else if (row?.affidavitertificateFile) {
            downloadPdfFile(row.affidavitertificateFile);
          }
        };

        return (
          <div className="flex gap-3 items-center">
            <span
              className="text-green-500 font-medium flex gap-1 items-center cursor-pointer"
              onClick={handleDownload}
            >
              Download
            </span>
            {/* Reopen with the saved details and generate again. */}
            <span
              className="text-blue-600 font-medium cursor-pointer"
              onClick={() => setUpdateRow(row)}
            >
              Update
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <SideNavLayout>
      <GeneratedCertificate
        columns={getColumns(columns, "AFFIDAVIT_CERTIFICATE")}
        rows={items.map((item: any) => ({
          ...item,
          key: item._id || item.srn,
        }))}
        onExportXlsx={handleExportXlsx}
        isExporting={isExporting}
      />
      {updateRow && (
        <DownloadDialogWrapper
          onClose={() => setUpdateRow(null)}
          businessEnquiryData={updateRow}
          isUpdate
        />
      )}
    </SideNavLayout>
  );
};

export default GeneratedCertificateWrapper;
